import { useState } from 'react'
import {
  Stack, Text, Group, TextInput, Button, Box, Badge,
} from '@mantine/core'
import {
  IconArrowsExchange,
  IconBuilding,
  IconDeviceFloppy,
  IconEdit,
  IconX,
} from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { api } from '@/api/client'
import { permissionsApi } from '@/api/permissions'
import type { UserRow } from './UsersTable'

interface UserDrawerGeneralProps {
  user: UserRow
  onNavigateToOrgs?: () => void
}

function formatDate(iso?: string | null): string {
  if (!iso) return 'Nunca'
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

export function UserDrawerGeneral({ user, onNavigateToOrgs }: UserDrawerGeneralProps) {
  const [editing, setEditing] = useState(false)
  const [fullName, setFullName] = useState(user.full_name)
  const [email, setEmail] = useState(user.email)
  const qc = useQueryClient()

  const { data: orgData, isLoading: loadingUserOrg } = useQuery({
    queryKey: ['user-organizations', user.id],
    queryFn: () => permissionsApi.getUserOrganizations(user.id),
    enabled: !!user.id,
  })
  const assignedOrgs = orgData?.assigned ?? []

  const { data: userPerms } = useQuery({
    queryKey: ['user-permissions', user.id],
    queryFn: () => permissionsApi.getUserPermissions(user.id),
  })
  // userPerms used by other parts of the drawer (template context) — keep query alive
  void userPerms

  const startEditing = () => {
    setFullName(user.full_name)
    setEmail(user.email)
    setEditing(true)
  }

  const cancelEditing = () => {
    setEditing(false)
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      await api.put(`/v1/users/${user.id}`, { full_name: fullName, email })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      notifications.show({ color: 'teal', title: 'Salvo', message: 'Dados atualizados com sucesso.' })
      setEditing(false)
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail ?? 'Erro ao salvar.'
      notifications.show({ color: 'red', title: 'Erro', message: detail })
    },
  })

  if (editing) {
    return (
      <Stack gap="md" p="md">
        <TextInput
          label="Nome completo"
          value={fullName}
          onChange={(e) => setFullName(e.currentTarget.value)}
        />
        <TextInput
          label="Email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
        />
        <Group gap="sm" mt="xs">
          <Button
            size="sm"
            leftSection={<IconDeviceFloppy size={14} />}
            onClick={() => saveMutation.mutate()}
            loading={saveMutation.isPending}
          >
            Salvar
          </Button>
          <Button
            size="sm"
            variant="subtle"
            color="gray"
            leftSection={<IconX size={14} />}
            onClick={cancelEditing}
            disabled={saveMutation.isPending}
          >
            Cancelar
          </Button>
        </Group>
      </Stack>
    )
  }

  return (
    <Stack gap="md" p="md">
      <Box>
        <Text size="xs" c="var(--ai-text-muted)" fw={500}>Nome completo</Text>
        <Text size="sm">{user.full_name || '—'}</Text>
      </Box>
      <Box>
        <Text size="xs" c="var(--ai-text-muted)" fw={500}>Email</Text>
        <Text size="sm">{user.email}</Text>
      </Box>
      <Box>
        <Text size="xs" c="var(--ai-text-muted)" fw={500}>Criado em</Text>
        <Text size="sm">{formatDate(user.created_at)}</Text>
      </Box>
      <Box>
        <Text size="xs" c="var(--ai-text-muted)" fw={500}>Último acesso</Text>
        <Text size="sm">{formatDate(user.last_login_at)}</Text>
      </Box>

      {/* Organização (read-only — editar na aba Organizações) */}
      <Box>
        <Group gap={6} mb={6}>
          <IconBuilding size={14} style={{ color: 'var(--ai-text-muted)' }} />
          <Text size="xs" c="var(--ai-text-muted)" fw={500}>Organizações</Text>
        </Group>
        {loadingUserOrg ? (
          <Text size="sm" c="dimmed">Carregando...</Text>
        ) : assignedOrgs.length > 0 ? (
          <Group gap={6}>
            {assignedOrgs.map((o) => (
              <Badge key={o.id} size="sm" variant="light" color="blue">{o.name}</Badge>
            ))}
          </Group>
        ) : (
          <Text size="sm" c="dimmed">Nenhuma organização atribuída.</Text>
        )}
        <Button
          size="compact-xs"
          variant="subtle"
          color="blue"
          leftSection={<IconArrowsExchange size={12} />}
          mt={6}
          onClick={() => onNavigateToOrgs?.()}
        >
          Editar organizações
        </Button>
      </Box>

      <Button
        variant="subtle"
        size="sm"
        leftSection={<IconEdit size={14} />}
        onClick={startEditing}
        mt="xs"
        style={{ alignSelf: 'flex-start' }}
      >
        Editar dados pessoais
      </Button>
    </Stack>
  )
}
